import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from './Logo.png';
import "./Navbar.css";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navbarRef = useRef(null);

  // إغلاق navbar عند تغيير الصفحة
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // إغلاق navbar عند التمرير على الهاتف
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      
      // إغلاق navbar عند التمرير على الهاتف إذا كان مفتوحاً
      if (window.innerWidth <= 992 && isOpen) {
        setIsOpen(false);
      }
    };

    // إغلاق navbar عند النقر خارجها
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // إغلاق navbar عند الضغط على زر الهاتف الخلفي
    const handleBackButton = () => {
      setIsOpen(false);
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('popstate', handleBackButton);

    // منع التمرير عند فتح navbar على الهاتف
    if (isOpen && window.innerWidth <= 992) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('popstate', handleBackButton);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const toggleNavbar = () => {
    setIsOpen(!isOpen);
  };

  const closeNavbar = () => {
    setIsOpen(false);
  };

  return (
    <nav 
      ref={navbarRef}
      className={`navbar navbar-expand-lg navbar-dark fixed-top custom-navbar ${isScrolled ? 'navbar-scrolled' : ''} ${isOpen ? 'navbar-open' : ''}`}
    >
      <div className="container d-flex align-items-center">
        {/* LEFT: Logo */}
        <Link 
          className="navbar-brand d-flex align-items-center" 
          to="/" 
          onClick={closeNavbar}
        >
          <img src={Logo} alt="Realm Of Herbs Logo" className="nav-logo" />
          <span className="brand-text d-none d-md-inline">Realm Of Herbs</span>
        </Link>

        {/* Mobile Toggler Button */}
        <button
          className={`navbar-toggler ${isOpen ? 'open' : ''}`}
          type="button"
          onClick={toggleNavbar}
          aria-label="Toggle navigation"
          aria-expanded={isOpen}
        >
          <span className="navbar-toggler-icon">
            <span className="toggler-bar"></span>
            <span className="toggler-bar"></span>
            <span className="toggler-bar"></span>
          </span>
        </button>

        {/* CENTER: Menu */}
        <div 
          className={`collapse navbar-collapse ${isOpen ? 'show' : ''}`}
          id="navbarNav"
        >
          <ul className="navbar-nav mx-auto text-center text-lg-start">
            <li className="nav-item">
              <Link 
                className={`nav-link ${location.pathname === '/' ? 'active' : ''}`} 
                to="/" 
                onClick={closeNavbar}
              >
                <i className="fas fa-home d-lg-none me-2"></i>
                Home
              </Link>
            </li>

            <li className="nav-item">
              <Link 
                className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`} 
                to="/about" 
                onClick={closeNavbar}
              >
                <i className="fas fa-info-circle d-lg-none me-2"></i>
                About
              </Link>
            </li>

            <li className="nav-item">
              <Link 
                className={`nav-link ${location.pathname === '/products' ? 'active' : ''}`} 
                to="/products" 
                onClick={closeNavbar}
              >
                <i className="fas fa-leaf d-lg-none me-2"></i>
                Products
              </Link>
            </li>

            <li className="nav-item">
              <Link 
                className={`nav-link ${location.pathname === '/gallery' ? 'active' : ''}`} 
                to="/gallery" 
                onClick={closeNavbar}
              >
                <i className="fas fa-images d-lg-none me-2"></i>
                Gallery
              </Link>
            </li>

            <li className="nav-item">
              <Link 
                className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`} 
                to="/contact" 
                onClick={closeNavbar}
              >
                <i className="fas fa-envelope d-lg-none me-2"></i>
                Contact
              </Link>
            </li>

            {/* Mobile Social Icons */}
            <div className="mobile-social-section d-lg-none mt-4 pt-4 border-top">
              <h6 className="text-center text-light mb-3">Follow Us</h6>
              <div className="d-flex justify-content-center gap-4 mb-4">
                <a 
                  href="https://www.facebook.com/share/1BzQdX7m8Z/" 
                  className="social-icon-mobile"
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={closeNavbar}
                >
                  <i className="fab fa-facebook-f"></i>
                </a>
                <a 
                  href="https://www.linkedin.com/in/sherif-ibrahim-18a61b234" 
                  className="social-icon-mobile"
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={closeNavbar}
                >
                  <i className="fab fa-linkedin-in"></i>
                </a>
                <a 
                  href="https://wa.me/201221691637" 
                  className="social-icon-mobile whatsapp-icon"
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={closeNavbar}
                >
                  <i className="fab fa-whatsapp"></i>
                </a>
              </div>
              
              {/* Mobile Contact Info */}
              <div className="mobile-contact-info text-center">
                <a 
                  href="tel:+201221691637" 
                  className="d-block mb-2 text-light"
                  onClick={closeNavbar}
                >
                  <i className="fas fa-phone-alt me-2"></i>
                  +20 122 169 1637
                </a>
                <a 
                  href="mailto:info@realmofherbs.com" 
                  className="d-block text-light"
                  onClick={closeNavbar}
                >
                  <i className="fas fa-envelope me-2"></i>
                  info@realmofherbs.com
                </a>
              </div>
            </div>
          </ul>
        </div>

        {/* RIGHT: Desktop Social Icons */}
        <div className="social-icons d-none d-lg-flex ms-auto gap-3">
          <a 
            href="https://www.facebook.com/share/1BzQdX7m8Z/" 
            className="social-icon" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <i className="fab fa-facebook"></i>
          </a>
          <a 
            href="https://www.linkedin.com/in/sherif-ibrahim-18a61b234" 
            className="social-icon" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <i className="fab fa-linkedin"></i>
          </a>
          <a 
            href="https://wa.me/201221691637" 
            className="social-icon" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <i className="fab fa-whatsapp"></i>
          </a>
        </div>
      </div>
      
      {/* Mobile Navbar Overlay */}
      {isOpen && window.innerWidth <= 992 && (
        <div className="navbar-overlay" onClick={closeNavbar}></div>
      )}
    </nav>
  );
};

export default Navbar;